import { Component } from '@angular/core';
import {
  DriverAverageWastedTime,
  DriverLoss,
  DriverRejectedOrders,
  DriverShiftDuration,
  DriverTier,
  LohiDashboardService,
} from '../../lohi-dashboard.service';

interface DriverPerformanceMetrics {
  driverId: string;
  driverName: string;
  driverTier: DriverTier;
  avgWastedTime: DriverAverageWastedTime;
  avgShiftDur: DriverShiftDuration;
  driverLosses: DriverLoss;
  rejectedLoads: DriverRejectedOrders;
  overallMissedRevenue: number;
}

@Component({
  selector: 'lohi-app-carrier-performance',
  templateUrl: './carrier-performance-lost-revenue.component.html',
  styleUrls: ['./carrier-performance-lost-revenue.component.scss'],
})
export class CarrierPerformanceLostRevenueComponent {
  public driverPerformanceMetrics: DriverPerformanceMetrics[] | null = null;
  public selectedDriver: DriverPerformanceMetrics | null = null;

  constructor(private dashboardService: LohiDashboardService) {
    this.loadDriverMetrics();
  }

  public loadDriverMetrics(): void {
    this.dashboardService.getLostRevenueByDriver$().subscribe(
      (data) => {
        this.driverPerformanceMetrics = data.map((driver) => ({
          driverId: driver.driverId,
          driverName: driver.driverName,
          driverTier: driver.driverTier,
          driverLosses: driver.driverLosses,
          avgWastedTime: driver.avgWastedTime,
          avgShiftDur: {
            ...driver.avgShiftDur,
            totalAvgShiftDurationFormatted: this.formatAvgDuration(driver.avgShiftDur.totalAverageShiftDuration),
            shiftDetailsFormatted: driver.avgShiftDur.shifts
              ? driver.avgShiftDur.shifts.map((shift) =>
                  this.formatShiftDetails(shift.startTimestamp, shift.endTimestamp, shift.shiftDuration),
                )
              : [],
          },
          rejectedLoads: {
            ...driver.rejectedLoads,
            orderDetailsFormatted: driver.rejectedLoads?.orderDetails
              ? driver.rejectedLoads.orderDetails.map((order) => this.formatRejectedLoad(order))
              : [],
          },
          overallMissedRevenue: driver.overallMissedRevenue,
        }));

        this.driverPerformanceMetrics.sort((a, b) => b.overallMissedRevenue - a.overallMissedRevenue);

        if (this.driverPerformanceMetrics.length > 0) {
          this.selectedDriver = this.driverPerformanceMetrics[0];
        }
      },
      (error) => {
        console.error('Error fetching lost revenue data:', error);
      },
    );
  }

  public selectDriver(driver: DriverPerformanceMetrics) {
    this.selectedDriver = driver;
  }

  private formatRejectedLoad(order: {
    shiftId: string;
    loadsRejected: number;
    totalShiftTime: number;
    orderId?: string;
    pickUpName?: string;
    dropOffName?: string;
    total_missed_revenue?: number;
  }): string {
    const orderId = order.orderId || 'Unknown Order';
    const pickUpName = order.pickUpName || 'Unknown Pickup';
    const dropOffName = order.dropOffName || 'Unknown Dropoff';
    return `Rejected ${orderId} [${pickUpName} -> ${dropOffName}]`;
  }

  private formatAvgDuration(totalAverageShiftDuration: number): string {
    const hours = Math.floor(totalAverageShiftDuration / 60);
    const minutes = Math.round(totalAverageShiftDuration % 60);

    const formattedDuration = `${hours}h ${minutes}m`;

    return formattedDuration;
  }

  public getMaxMissedRevenue(): number {
    return this.driverPerformanceMetrics.length > 0
      ? Math.max(...this.driverPerformanceMetrics.map((metric) => metric.overallMissedRevenue || 0))
      : 0;
  }

  public getBarInfo(driver: DriverPerformanceMetrics) {
    const maxRevenueInCents = this.getMaxMissedRevenue();
    const missedPercent = driver.overallMissedRevenue ? (driver.overallMissedRevenue / maxRevenueInCents) * 100 : 0;

    return { missedPercent };
  }

  public trackByDriverID(index: number, item: DriverPerformanceMetrics) {
    return item.driverId;
  }

  public trackByIndex(index: number) {
    return index;
  }

  public highlightLLLs(message: string) {
    return message.replace(/(LLL\S{3,})/g, '<span class="text-blue-500 font-medium">$1</span>');
  }

  private extractDateAndTime(timestamp) {
    const dateObj = new Date(timestamp);
    const date = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`; // MM/DD format
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const time = `${formattedHours}:${minutes} ${period}`;
    return { date, time };
  }

  public formatShiftDetails(startTimestamp, endTimestamp, shiftDuration) {
    const startDetails = this.extractDateAndTime(startTimestamp);
    const endDetails = this.extractDateAndTime(endTimestamp);
    const avgShiftDuration = this.formatAvgDuration(shiftDuration);

    return `Shift on ${startDetails.date} started at ${startDetails.time} and ended at ${endDetails.time} (${avgShiftDuration})`;
  }
}
